import type { ComponentType, HTMLAttributes, ReactNode } from 'react';
import { CircleDashed } from 'lucide-react';
import { cn } from '../lib/cn';

/**
 * O "—" QUE SE EXPLICA (G6, 07/10/2026).
 *
 * É o `EmptyState kind="no-data"` em formato de linha, para quando o vazio tem causa
 * CONHECIDA e conserto: "Vincule uma campanha da Meta para ver quanto custa cada entrada."
 * + **Vincular campanha**. O tooltip solto em cima do travessão ensinava o que fazer e
 * escondia onde fazer; aqui a frase e o botão estão juntos.
 *
 * Regras de texto: diz o que falta e o que se ganha; nunca fala em sincronização, API ou id.
 * O botão é SECUNDÁRIO por padrão — a cor de ação da tela continua sendo uma só.
 */
export interface MissingDataHintProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  /** A frase: o que falta para o número existir. */
  children: ReactNode;
  /** No máximo um botão/link que resolve. */
  action?: ReactNode;
  icon?: ComponentType<{ className?: string }>;
  /** `brand` = sobre o herói violeta. */
  tone?: 'default' | 'brand';
}

export function MissingDataHint({
  children,
  action,
  icon: Icone = CircleDashed,
  tone = 'default',
  className,
  ...props
}: MissingDataHintProps) {
  const brand = tone === 'brand';
  return (
    <div
      data-kind="no-data"
      className={cn(
        'flex flex-col gap-2 rounded-md border border-dashed p-3 sm:flex-row sm:items-center sm:justify-between',
        brand
          ? 'border-primary-foreground/30 bg-primary-foreground/10 text-primary-foreground'
          : 'border-border-strong bg-surface-subtle text-foreground',
        className,
      )}
      {...props}
    >
      <p className="flex min-w-0 items-start gap-2 text-sm">
        <Icone
          aria-hidden="true"
          className={cn(
            'mt-0.5 size-4 shrink-0',
            brand ? 'text-primary-foreground/80' : 'text-muted-foreground',
          )}
        />
        <span>
          <span className="sr-only">Sem medição: </span>
          {children}
        </span>
      </p>
      {action ? <div className="shrink-0 max-sm:w-full [&>*]:max-sm:w-full">{action}</div> : null}
    </div>
  );
}
